import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { KnowledgeFile } from '../../types';
import { getFileIconByExtension, getFileIconColor } from '../../utils/file-types';
import { getPublicUrl } from '../../config/storage';
import { formatFileSize } from '../../utils/file-types';
import { MoreVertical, Star, Lock, Eye, Globe } from 'lucide-react';
import { useFileStore } from '../../stores/file-store';
import { useCategoryStore } from '../../stores/category-store';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Document, Page } from 'react-pdf';
import { cn } from '../../utils/cn';
import { useI18n } from '../../i18n';
import MoreMenu from '../shared/MoreMenu';
import { PERMISSION_LABELS, PERMISSION_COLORS } from '../../utils/permissions';
import FilePermissionModal from './FilePermissionModal';

dayjs.extend(relativeTime);

interface FileCardProps {
  file: KnowledgeFile;
  selectable?: boolean;
  selected?: boolean;
  onSelectChange?: (id: string, checked: boolean) => void;
  onRequestMove?: (id: string) => void;
  onRequestDelete?: (id: string) => void;
}

const FileCardInner: React.FC<FileCardProps> = ({ file, selectable = false, selected = false, onSelectChange, onRequestMove, onRequestDelete }) => {
  const navigate = useNavigate();
  // 只订阅需要的 store 方法，避免订阅整个 store 导致不必要的重渲染
  const starFile = useFileStore(state => state.starFile);
  const fetchCategories = useCategoryStore(state => state.fetchCategories);
  
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  // 使用基于文件名的图标，更精确
  const FileIcon = getFileIconByExtension(file.name);
  const iconColor = getFileIconColor(file.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const { t } = useI18n();
  
  // 记录上一次看到的缩略图键值，用于检测变化
  const lastThumbnailKeyRef = useRef<string | null>(file.thumbnail_key || null);
  // 标记是否已经加载过缩略图
  const thumbnailLoadedRef = useRef(false);

  // 点击空白自动关闭卡片右上角的菜单
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen]);
  
  // 单一的缩略图加载 effect - 合并所有缩略图加载逻辑
  useEffect(() => {
    // 如果缩略图 key 没有变化且已经加载过，跳过
    const currentKey = file.thumbnail_key || null;
    if (currentKey === lastThumbnailKeyRef.current && thumbnailLoadedRef.current) {
      return;
    }
    
    // 更新 ref
    lastThumbnailKeyRef.current = currentKey;
    
    // 如果有缩略图 key，直接加载
    if (currentKey) {
      let cancelled = false;
      
      getPublicUrl(currentKey).then(url => {
        if (cancelled) return;
        
        // 预加载图片
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          setThumbnailUrl(url);
          setImageLoaded(true);
          thumbnailLoadedRef.current = true;
        };
        img.onerror = () => {
          if (cancelled) return;
          setThumbnailUrl(null);
          setImageError(true);
        };
        img.src = url;
      });
      
      return () => { cancelled = true; };
    }
    
    // 没有缩略图 key，尝试加载原始文件作为预览
    if ((file.type === 'image' || file.type === 'video' || file.type === 'pdf') && file.storage_key) {
      let cancelled = false;
      
      getPublicUrl(file.storage_key).then(url => {
        if (cancelled) return;
        
        if (file.type === 'image') {
          setImageUrl(url);
        } else if (file.type === 'video') {
          setVideoThumbnail(url);
        } else if (file.type === 'pdf') {
          setPdfUrl(url);
        }
        thumbnailLoadedRef.current = true;
      }).catch(() => {
        if (cancelled) return;
        setImageError(true);
      });
      
      return () => { cancelled = true; };
    }
  }, [file.id, file.thumbnail_key, file.type, file.storage_key]);
  
  const handleReloadImage = useCallback(() => {
    setImageLoaded(false);
    setImageError(false);
    thumbnailLoadedRef.current = false;
    // 清除URL，强制重新加载
    setThumbnailUrl(null);
    setImageUrl(null);
    setVideoThumbnail(null);
    setPdfUrl(null);
  }, []);
  
  const renderPreview = () => {
    if (thumbnailUrl) {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50 overflow-hidden">
          <img
            src={thumbnailUrl}
            alt={file.name}
            className={cn(
              "max-w-full max-h-full object-contain transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setImageLoaded(true)}
            onError={() => {
              console.log(`缩略图加载失败: ${thumbnailUrl}`);
              setImageError(true);
              handleReloadImage();
            }}
          />
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-4 border-gray-200 border-t-teal-500 rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      );
    }
    
    if (file.type === 'image') {
      return imageUrl ? (
        !imageError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
          <img
            src={imageUrl}
            loading="lazy"
            alt={file.name}
            className={cn(
                "max-w-full max-h-full object-contain transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0"
            )}
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
          />
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
            <FileIcon size={32} className="text-gray-400" />
          </div>
        )
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <FileIcon size={32} className="text-gray-400" />
        </div>
      );
    }
    
    if (file.type === 'video') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          {videoThumbnail ? (
            <img
              src={videoThumbnail}
              alt={file.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <FileIcon size={32} className="text-gray-400" />
            </div>
          )}
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white bg-opacity-75 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[12px] border-l-gray-800 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent ml-1" />
            </div>
          </div>
        </div>
      );
    }
    
    if (file.type === 'pdf') {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          {pdfUrl ? (
            <Document
              file={pdfUrl}
              loading={
                <div className="w-full h-full flex items-center justify-center">
                  <FileIcon size={32} className="text-gray-400" />
                </div>
              }
            >
              <Page
                pageNumber={1}
                width={280}
                height={180}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                scale={1}
              />
            </Document>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <FileIcon size={32} className="text-gray-400" />
            </div>
          )}
        </div>
      );
    }
    
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className={cn(
            "flex items-center justify-center w-16 h-16 rounded-xl",
            iconColor.bg
          )}
        >
          <FileIcon 
            size={32} 
            className={iconColor.text}
          />
        </div>
      </div>
    );
  };
  
  const handleCardClick = useCallback(() => {
    if (selectable) return; // 在选择模式下，点击卡片不跳转
    navigate(`/uadmin/files/${file.id}`);
  }, [selectable, navigate, file.id]);
  
  const handleStarClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    starFile(file.id, !file.starred);
    (async () => {
      // Update both files and categories
      await Promise.all([
        useFileStore.getState().fetchFiles(),
        fetchCategories()
      ]);
    })().catch((err: unknown) => {
      console.error('Error starring file:', err);
    });
  }, [file.id, file.starred, starFile, fetchCategories]);
  
  const handleMoreClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // 计算按钮位置
    if (menuButtonRef.current) {
      setTriggerRect(menuButtonRef.current.getBoundingClientRect());
    }
    setMenuOpen((v) => !v);
  }, []);
  
  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "relative flex flex-col overflow-hidden transition-all bg-white border border-gray-200 rounded-lg shadow-sm group hover:shadow-md cursor-pointer",
        selected && "ring-2 ring-teal-500 border-teal-400"
      )}
    >
      {selectable && (
        <div className="absolute top-2 left-2 z-20">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onSelectChange?.(file.id, e.target.checked);
            }}
            className="h-4 w-4 accent-teal-600 cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <div className="relative aspect-video bg-gray-50 overflow-hidden">
        {renderPreview()}
        {selected && (
          <div className="absolute inset-0 bg-teal-50/30 pointer-events-none" />
        )}
        
        <div
          ref={menuRef}
          className="absolute top-2 right-2 flex space-x-2 transition-opacity md:opacity-0 md:group-hover:opacity-100 opacity-100"
        >
          <button
            onClick={handleStarClick}
            title={file.starred ? t('removeFromStarred') : t('addToStarred')}
            className={cn(
              'flex items-center justify-center rounded-full transition-colors p-2 min-w-[44px] min-h-[44px]',
              file.starred
                ? 'text-yellow-600 bg-yellow-50'
                : 'text-gray-600 bg-white/80 hover:text-yellow-600'
            )}
          >
            <Star size={18} fill={file.starred ? 'currentColor' : 'none'} />
          </button>

          <button
            ref={menuButtonRef}
            onClick={handleMoreClick}
            title={t('more')}
            className="flex items-center justify-center p-2 rounded-full text-gray-600 transition-colors bg-white/80 hover:text-gray-800 min-w-[44px] min-h-[44px]"
          >
            <MoreVertical size={18} />
          </button>
          {menuOpen && triggerRect && (
            <MoreMenu
              triggerRect={triggerRect}
              onPreview={() => {
                setMenuOpen(false);
                navigate(`/uadmin/files/${file.id}`);
              }}
              onDownload={async () => {
                setMenuOpen(false);
                console.log('下载文件:', file.name, 'storage_key:', file.storage_key);
                if (!file.storage_key) {
                  const { showToast } = await import('../../utils/toast');
                  showToast('文件不可下载');
                  return;
                }
                try {
                  const { getSignedUrl } = await import('../../config/storage');
                  const { showToast } = await import('../../utils/toast');
                  showToast('正在下载...');
                  const url = await getSignedUrl(file.storage_key);
                  // Fetch the file content
                  const response = await fetch(url);
                  const blob = await response.blob();
                  // Create object URL for the blob
                  const objectUrl = window.URL.createObjectURL(blob);
                  // Create temporary link and trigger download
                  const link = document.createElement('a');
                  link.href = objectUrl;
                  link.download = file.name;
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  // Cleanup after a short delay
                  setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(objectUrl);
                  }, 100);
                } catch (err) {
                  console.error('下载失败:', err);
                  const { showToast } = await import('../../utils/toast');
                  showToast('下载失败，请重试');
                }
              }}
              onCopyLink={async () => {
                setMenuOpen(false);
                if (file.storage_key) {
                  try {
                    const { getPublicUrl } = await import('../../config/storage');
                    const { showToast } = await import('../../utils/toast');
                    const url = await getPublicUrl(file.storage_key);
                    await navigator.clipboard.writeText(url);
                    showToast('文件地址已复制');
                  } catch (err) {
                    console.error('复制失败:', err);
                    const { showToast } = await import('../../utils/toast');
                    showToast('复制失败，请重试');
                  }
                }
              }}
              onMove={() => {
                setMenuOpen(false);
                onRequestMove?.(file.id);
              }}
              onDelete={() => {
                setMenuOpen(false);
                onRequestDelete?.(file.id);
              }}
              onPermissions={() => {
                setMenuOpen(false);
                setShowPermissionModal(true);
              }}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </div>
      </div>
      
      <div className="flex flex-col flex-1 p-3">
        <h3 className="mb-1 text-sm font-medium text-gray-900 truncate" title={file.name}>
          {file.name}
        </h3>
        
        <div className="mt-auto">
          {/* 权限标识 */}
          <div className="flex items-center gap-2 mb-2">
            {file.owner_only ? (
              <div className="flex items-center text-purple-600" title="私密，仅创建者可见">
                <Lock size={12} className="mr-1" />
                <span className="text-xs">私密</span>
              </div>
            ) : file.is_public ? (
              <div className="flex items-center text-green-600" title="公开文件">
                <Globe size={12} className="mr-1" />
                <span className="text-xs">公开</span>
              </div>
            ) : file.min_permission_level ? (
              <div className="flex items-center" title={`需要${PERMISSION_LABELS[file.min_permission_level]}权限`}>
                <Lock size={12} className="mr-1 text-gray-400" />
                <span className={cn(
                  "text-xs px-1.5 py-0.5 rounded",
                  PERMISSION_COLORS[file.min_permission_level]
                )}>
                  {PERMISSION_LABELS[file.min_permission_level]}
                </span>
              </div>
            ) : (
              <div className="flex items-center text-gray-500" title="基础权限">
                <Eye size={12} className="mr-1" />
                <span className="text-xs">基础</span>
              </div>
            )}
          </div>
          
          <div className="flex flex-wrap gap-1 mb-2">
            {(file.tags || []).slice(0, 2).map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center px-2 py-0.5 text-xs rounded-full"
                style={{ backgroundColor: `${tag.color}20`, color: tag.color }}
              >
                {tag.name}
              </span>
            ))}
            {file.tags && file.tags.length > 2 && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                +{file.tags.length - 2}
              </span>
            )}
          </div>
          
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="truncate capitalize">{file.type}</span>
            <span>{formatFileSize(file.size)}</span>
          </div>
        </div>
      </div>
      
      {/* 权限设置模态框 */}
      {showPermissionModal && (
        <FilePermissionModal
          file={file}
          onClose={() => setShowPermissionModal(false)}
          onUpdate={() => {
            // 刷新文件列表
            useFileStore.getState().fetchFiles();
          }}
        />
      )}
    </div>
  );
};

// 使用 React.memo 包装组件，只在关键 props 变化时重新渲染
const FileCard = React.memo(FileCardInner, (prevProps, nextProps) => {
  // 返回 true 表示 props 相等，不需要重新渲染
  return (
    prevProps.file.id === nextProps.file.id &&
    prevProps.file.name === nextProps.file.name &&
    prevProps.file.starred === nextProps.file.starred &&
    prevProps.file.thumbnail_key === nextProps.file.thumbnail_key &&
    prevProps.file.is_public === nextProps.file.is_public &&
    prevProps.file.owner_only === nextProps.file.owner_only &&
    prevProps.file.min_permission_level === nextProps.file.min_permission_level &&
    prevProps.selectable === nextProps.selectable &&
    prevProps.selected === nextProps.selected
  );
});

export default FileCard;